'use client';

import { useCallback, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import {
  Button,
  IsVisible,
  PasswordField,
  TextField,
  Toast,
} from '@packages/ui';
import { useRouter } from 'next/navigation';
import { Controller, useForm } from 'react-hook-form';
import * as Yup from 'yup';

import { EActiveDomains } from 'enums/activeDomain';
import { signInClientRequest } from 'services/httpRequests/auth/signIn/request.client';

interface ILoginFormValues {
  email: string;
  password: string;
}

interface ILoginFormProps {
  activeDomain: EActiveDomains;
  dictionary: any;
  lang: string;
}

const authenticateSchema = Yup.object({}).shape({
  email: Yup.string()
    .email('This is not a valid email')
    .required('Required field'),
  password: Yup.string().required('Required field'),
});

export function LoginForm({ activeDomain, dictionary, lang }: ILoginFormProps) {
  const router = useRouter();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { control, handleSubmit } = useForm<ILoginFormValues>({
    resolver: yupResolver(authenticateSchema),
  });

  const handleLogin = useCallback(
    async (values: ILoginFormValues) => {
      try {
        setIsLoading(true);

        await signInClientRequest({
          locale: lang || 'fr',
          data: {
            email: values.email,
            password: values.password,
            activeDomain,
          },
          functions: {
            navigateTo: url => {
              router.push(url);
              router.refresh();
            },
            onStopLoading: () => {
              setIsLoading(false);
            },
          },
        });
      } catch (err: any) {
        setIsLoading(false);
        Toast.warning({
          toastMessage: err.message,
        });
      }
    },
    [activeDomain, lang, router],
  );

  return (
    <form
      className="flex flex-col gap-1 w-full"
      onSubmit={handleSubmit(handleLogin)}
    >
      <Controller
        control={control}
        name="email"
        render={({ field, fieldState }) => (
          <TextField
            containerProps={{
              className: 'bg-white rounded-md h-14',
              error: fieldState.error?.message,
            }}
            name={field.name}
            onChange={field.onChange}
            placeholder="Email"
          />
        )}
      />
      <Controller
        control={control}
        name="password"
        render={({ field, fieldState }) => (
          <PasswordField
            containerProps={{
              className: 'bg-white rounded-md h-14',
              rest: {
                error: fieldState.error?.message,
              },
            }}
            name={field.name}
            onChange={field.onChange}
            placeholder={dictionary?.login?.password || 'Password'}
          />
        )}
      />

      <IsVisible when={!!dictionary}>
        <Button
          className="w-1/2 mt-4 mx-auto rounded text-white border-white"
          design="outline"
          isLoading={isLoading}
          type="submit"
        >
          {dictionary?.login?.login ?? 'Login'}
        </Button>
      </IsVisible>
    </form>
  );
}
